import React from 'react';
import { Repossession, RepossessionStatus } from '@/types/common';

interface StatusNotificationProps {
  repoCase: Repossession;
  now: number;
}

const getCountdownText = (repoCase: Repossession, now: number) => {
  const dueAt = repoCase.escalationDueAt;
  if (!dueAt) return null;

  const dueMs = new Date(dueAt).getTime();
  if (Number.isNaN(dueMs)) return null;

  const remainingMs = Math.max(0, dueMs - now);

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `Time Remaining: ${hours} Hour ${minutes} Minutes ${seconds} Seconds Until Automatic Escalation to POTENSI KEHILANGAN`;
};

const StatusNotification: React.FC<StatusNotificationProps> = ({
  repoCase,
  now,
}) => {
  const escalationLabel =
    repoCase.escalationTrigger === 'MANUAL'
      ? 'Manually Escalated'
      : repoCase.escalationTrigger === 'AUTOMATIC'
        ? 'Automatically Escalated'
        : 'Escalated';
  const actionRequiredText =
    '⚠️ ACTION REQUIRED: Ops team should intensify search efforts and allocate resources. When search is exhausted, manually escalate to Fleet Manager for approval.';

  const getPotentialLossMessage = () => {
    if (repoCase.escalationTrigger === 'MANUAL') {
      const actor = repoCase.updatedBy || 'system';
      return `Case manually escalated to Potential Loss prior to the 48-hour automated threshold by ${actor}.
    ${actionRequiredText}`;
    }

    return `Vehicle not found after 48 hours. Case automatically escalated by system.
    ${actionRequiredText}`;
  };

  if (repoCase.status === RepossessionStatus.POTENTIAL_LOSS) {
    return (
      <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-6 w-7 items-center justify-center rounded-full border border-red-500 bg-red-100 text-red-600">
            <span className="text-xs font-bold">!</span>
          </span>
          <div>
            <p className="text-sm font-semibold">
              Potential Loss Case - {escalationLabel}
            </p>
            <p className="text-xs whitespace-pre-line">
              {getPotentialLossMessage()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (repoCase.status === RepossessionStatus.PENDING_APPROVAL) {
    return (
      <div className="mt-4 rounded-xl border border-purple-100 bg-[#EFEAF7] px-4 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-purple-600">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-semibold">Awaiting Approval</p>
            <p className="text-xs">
              Case is pending approval before it can be closed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (repoCase.status === RepossessionStatus.IN_PROGRESS) {
    const countdown = getCountdownText(repoCase, now);

    return (
      <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6l4 2"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-medium text-gray-900">
              48 Hour Auto Escalation Timer
            </p>
            <p className="text-xs font-medium text-gray-500">
              {countdown || 'Countdown unavailable.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default StatusNotification;
